<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';

import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import { searchContacts } from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';

const props = defineProps({
  toEmails: { type: String, default: '' },
  ccEmails: { type: String, default: '' },
  bccEmails: { type: String, default: '' },
  emailSubject: { type: String, default: '' },
  includeOriginalMessage: { type: Boolean, default: true },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:toEmails',
  'update:ccEmails',
  'update:bccEmails',
  'update:includeOriginalMessage',
  'update:emailSubject',
]);

const { t } = useI18n();
const isCompressed = ref(true);
const showCc = ref(false);
const showBcc = ref(false);
const contacts = ref([]);
const isSearching = ref(false);
const showToDropdown = ref(false);
const showCcDropdown = ref(false);
const showBccDropdown = ref(false);
const splitEmails = str =>
  str
    ? str
        .split(',')
        .map(e => e.trim())
        .filter(Boolean)
    : [];

const toEmailsArray = computed(() => splitEmails(props.toEmails));
const ccEmailsArray = computed(() => splitEmails(props.ccEmails));
const bccEmailsArray = computed(() => splitEmails(props.bccEmails));
const contactEmailsList = computed(() =>
  (contacts.value || [])
    .filter(c => c.email)
    .map(({ name, id, email }) => ({
      id,
      label: email,
      email,
      thumbnail: { name: name || email, src: '' },
      value: id,
      action: 'email',
    }))
);

const performSearch = debounce(async query => {
  if (!query || query.trim().length < 3) {
    contacts.value = [];
    return;
  }
  isSearching.value = true;
  try {
    contacts.value = await searchContacts({
      keys: ['email', 'name'],
      query: query.trim(),
    });
  } catch {
    contacts.value = [];
  } finally {
    isSearching.value = false;
  }
}, 300);

const dropdownRefs = {
  to: showToDropdown,
  cc: showCcDropdown,
  bcc: showBccDropdown,
};

const handleSearchInput = (field, value) => {
  dropdownRefs[field].value = true;
  performSearch(value);
};

const handleFocus = field => {
  dropdownRefs[field].value = true;
};

const handleClickOutside = field => {
  dropdownRefs[field].value = false;
};

const onToAdd = () => {
  nextTick(() => emit('update:toEmails', toEmailsArray.value.join(', ')));
};
const onToRemove = () => {
  emit('update:toEmails', toEmailsArray.value.join(', '));
};
const onCcAdd = () => {
  nextTick(() => emit('update:ccEmails', ccEmailsArray.value.join(', ')));
};
const onCcRemove = () => {
  emit('update:ccEmails', ccEmailsArray.value.join(', '));
};
const onBccAdd = () => {
  nextTick(() => emit('update:bccEmails', bccEmailsArray.value.join(', ')));
};
const onBccRemove = () => {
  emit('update:bccEmails', bccEmailsArray.value.join(', '));
};

const handleExpand = () => {
  isCompressed.value = false;
};
const handleCompress = () => {
  isCompressed.value = true;
};
const handleAddCc = () => {
  showCc.value = true;
};
const handleAddBcc = () => {
  showBcc.value = true;
};
const handleHideCc = () => {
  showCc.value = false;
  showBcc.value = false;
};
const handleHideBcc = () => {
  showBcc.value = false;
};

const canHideBcc = () => !props.bccEmails || props.bccEmails.length === 0;
const canHideCc = () =>
  (!props.ccEmails || props.ccEmails.length === 0) && canHideBcc();

const resetUIState = () => {
  if (props.bccEmails && props.bccEmails.length > 0) {
    showCc.value = true;
    showBcc.value = true;
  } else if (props.ccEmails && props.ccEmails.length > 0) {
    showCc.value = true;
    showBcc.value = false;
  } else {
    showCc.value = false;
    showBcc.value = false;
  }
};

const emailSubjectVal = ref(props.emailSubject);

watch(
  () => props.emailSubject,
  newVal => {
    if (newVal !== emailSubjectVal.value) {
      emailSubjectVal.value = newVal;
    }
  }
);

const onSubjectBlur = () => {
  emit('update:emailSubject', emailSubjectVal.value);
};

const includeOriginalMessageVal = ref(props.includeOriginalMessage);

watch(
  () => props.includeOriginalMessage,
  newVal => {
    if (newVal !== includeOriginalMessageVal.value) {
      includeOriginalMessageVal.value = newVal;
    }
  }
);

const onIncludeOriginalMessageChange = () => {
  emit('update:includeOriginalMessage', includeOriginalMessageVal.value);
};

watch(() => props.ccEmails, resetUIState);
watch(() => props.bccEmails, resetUIState);

resetUIState();
</script>

<template>
  <div class="reply-email-head">
    <!-- Compressed view -->
    <div v-if="isCompressed">
      <div class="input-group small compressed-view">
        <div v-if="isLoading && !toEmails" class="flex-1 min-w-0">
          <div class="skeleton-loader" />
          <div class="skeleton-loader mt-1" />
        </div>
        <div v-else class="flex-1 min-w-0">
          <div v-if="emailSubjectVal" class="email-block">
            <span class="email-field">
              <span class="email-label">{{
                t('CONVERSATION.REPLYBOX.EMAIL_HEAD.SUBJECT.LABEL')
              }}</span>
              <span class="email-value">
                {{ t('CONVERSATION.REPLYBOX.EMAIL_HEAD.SUBJECT.RE_PREFIX') }}
                {{ emailSubjectVal }}
              </span>
            </span>
          </div>
          <div class="email-block">
            <span class="email-field">
              <span class="email-label">{{
                t('CONVERSATION.REPLYBOX.EMAIL_HEAD.TO')
              }}</span>
              <span class="email-value">{{ toEmails }}</span>
            </span>
            <span v-if="ccEmails" class="email-field">
              <span class="email-label">{{
                t('CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.LABEL')
              }}</span>
              <span class="email-value">{{ ccEmails }}</span>
            </span>
            <span v-if="bccEmails" class="email-field">
              <span class="email-label">{{
                t('CONVERSATION.REPLYBOX.EMAIL_HEAD.BCC.LABEL')
              }}</span>
              <span class="email-value">{{ bccEmails }}</span>
            </span>
          </div>
        </div>
        <ButtonV4
          v-tooltip.top="$t('CONVERSATION.REPLYBOX.EMAIL_HEAD.EDIT_HEADERS')"
          icon="i-lucide-pencil"
          ghost
          class="text-n-slate-11"
          @click="handleExpand"
        />
      </div>
    </div>

    <!-- Expanded view -->
    <div v-if="!isCompressed" class="flex gap-2">
      <div class="flex-1 min-w-0">
        <div class="input-group small">
          <label class="input-group-label">
            {{ t('CONVERSATION.REPLYBOX.EMAIL_HEAD.SUBJECT.LABEL') }}
          </label>
          <div class="flex-1 min-w-0 m-0 rounded-none flex items-center">
            <span class="re-prefix">{{
              t('CONVERSATION.REPLYBOX.EMAIL_HEAD.SUBJECT.RE_PREFIX')
            }}</span>
            <input
              v-model="emailSubjectVal"
              type="text"
              class="subject-input"
              :placeholder="
                t('CONVERSATION.REPLYBOX.EMAIL_HEAD.SUBJECT.PLACEHOLDER')
              "
              @blur="onSubjectBlur"
            />
          </div>
        </div>
        <div class="input-group small">
          <label class="input-group-label">
            {{ t('CONVERSATION.REPLYBOX.EMAIL_HEAD.TO') }}
          </label>
          <div class="flex-1 min-w-0 m-0 rounded-none">
            <div class="flex items-center gap-2">
              <TagInput
                :model-value="toEmailsArray"
                :placeholder="
                  t('CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.PLACEHOLDER')
                "
                :menu-items="contactEmailsList"
                :show-dropdown="showToDropdown"
                :is-loading="isSearching"
                :min-tags="1"
                type="email"
                class="flex-1 min-h-7"
                @focus="handleFocus('to')"
                @input="handleSearchInput('to', $event)"
                @on-click-outside="handleClickOutside('to')"
                @add="onToAdd"
                @remove="onToRemove"
              />
              <ButtonV4
                v-if="!showCc"
                :label="t('CONVERSATION.REPLYBOX.EMAIL_HEAD.EXPAND')"
                ghost
                xs
                primary
                @click="handleAddCc"
              />
            </div>
          </div>
        </div>
        <div v-if="showCc" class="input-group-wrap">
          <div class="input-group small">
            <label class="input-group-label">
              {{ t('CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.LABEL') }}
            </label>
            <div class="flex-1 min-w-0 m-0 rounded-none">
              <TagInput
                :model-value="ccEmailsArray"
                :placeholder="
                  t('CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.PLACEHOLDER')
                "
                :menu-items="contactEmailsList"
                :show-dropdown="showCcDropdown"
                :is-loading="isSearching"
                type="email"
                class="flex-1"
                @focus="handleFocus('cc')"
                @input="handleSearchInput('cc', $event)"
                @on-click-outside="handleClickOutside('cc')"
                @add="onCcAdd"
                @remove="onCcRemove"
              />
            </div>
            <ButtonV4
              v-if="!showBcc"
              :label="t('CONVERSATION.REPLYBOX.EMAIL_HEAD.ADD_BCC')"
              ghost
              xs
              primary
              @click="handleAddBcc"
            />
            <ButtonV4
              v-if="canHideCc()"
              :label="t('CONVERSATION.REPLYBOX.EMAIL_HEAD.HIDE')"
              ghost
              xs
              secondary
              @click="handleHideCc"
            />
          </div>
        </div>

        <div v-if="showBcc" class="input-group-wrap">
          <div class="input-group small">
            <label class="input-group-label">
              {{ t('CONVERSATION.REPLYBOX.EMAIL_HEAD.BCC.LABEL') }}
            </label>
            <div class="flex-1 min-w-0 m-0 rounded-none">
              <TagInput
                :model-value="bccEmailsArray"
                :placeholder="
                  t('CONVERSATION.REPLYBOX.EMAIL_HEAD.BCC.PLACEHOLDER')
                "
                :menu-items="contactEmailsList"
                :show-dropdown="showBccDropdown"
                :is-loading="isSearching"
                type="email"
                class="flex-1"
                @focus="handleFocus('bcc')"
                @input="handleSearchInput('bcc', $event)"
                @on-click-outside="handleClickOutside('bcc')"
                @add="onBccAdd"
                @remove="onBccRemove"
              />
            </div>
            <ButtonV4
              v-if="canHideBcc()"
              :label="t('CONVERSATION.REPLYBOX.EMAIL_HEAD.HIDE')"
              ghost
              xs
              secondary
              @click="handleHideBcc"
            />
          </div>
        </div>
      </div>
      <ButtonV4
        v-tooltip.top="t('CONVERSATION.REPLYBOX.EMAIL_HEAD.HIDE_HEADER_EDITS')"
        icon="i-lucide-x"
        ghost
        class="self-center text-n-slate-11"
        @click="handleCompress"
      />
    </div>

    <div v-if="!isCompressed" class="checkbox-group">
      <label class="checkbox-wrapper">
        <input
          v-model="includeOriginalMessageVal"
          type="checkbox"
          class="checkbox"
          @change="onIncludeOriginalMessageChange"
        />
        <span class="checkbox-label">
          {{ t('CONVERSATION.REPLYBOX.EMAIL_HEAD.INCLUDE_ORIGINAL_MESSAGE') }}
        </span>
      </label>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.input-group-wrap .message {
  @apply text-sm text-n-ruby-8;
}
.input-group {
  @apply border-b border-solid border-n-weak mt-1 flex items-start gap-2 min-h-8 py-1;

  .input-group-label {
    @apply border-transparent bg-transparent text-xs font-semibold pl-0 self-center;
  }

  :deep(input:not(.checkbox)) {
    @apply h-auto;
  }
}

.input-group.error {
  @apply border-n-ruby-8;
  .input-group-label {
    @apply text-n-ruby-8;
  }
}

.checkbox-group {
  @apply border-b border-solid border-n-weak my-1 pb-2;
}

.checkbox-wrapper {
  @apply flex items-center gap-2 cursor-pointer;
}

.checkbox {
  @apply w-4 h-4 rounded focus:ring-2;
}

.checkbox-label {
  @apply text-xs font-medium;
}

.compressed-view {
  @apply items-center;
}

.email-block {
  @apply flex flex-wrap gap-x-4 gap-y-1 items-center;
}

.email-field {
  @apply flex items-center gap-1;
}

.email-label {
  @apply text-xs font-semibold;
}

.email-value {
  @apply text-sm text-slate-600 dark:text-slate-400;
}

.skeleton-loader {
  @apply h-4 w-48 rounded bg-n-slate-3 animate-pulse;
}

.re-prefix {
  @apply text-sm text-n-slate-11 flex-shrink-0 mr-1;
}

.subject-input {
  width: 100% !important;
  height: auto !important;
  border: 0 !important;
  background: transparent !important;
  font-size: 0.875rem !important;
  outline: none !important;
  padding: 0 !important;
  margin: 0 !important;
  box-shadow: none !important;
  color: #000 !important;

  &:focus {
    border: 0 !important;
    outline: none !important;
    box-shadow: none !important;
  }
}
</style>
